import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { IBANInput } from '@adornis/formfield/fields/iban-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { formatCalendarDay, todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { HealthInsurance, HealthInsuranceTypes } from '../entities/HealthInsurance.js';

const HEALTH_INSURANCE_SELECTION = selectionSet(() => HealthInsurance, 1);

/**
 * `registerEnum` writes `_class` and `_isBaseQLEnum` onto the enum object, so plain `Object.values`
 * also yields that registration metadata. Filtering by key is stable, unlike the previous filter
 * which listed the metadata *values* (`'HealthInsuranceTypes'`, `true`) explicitly.
 */
const healthInsuranceTypeSelectables = Object.entries(HealthInsuranceTypes)
  .filter(([key]) => !key.startsWith('_'))
  .map(([, value]) => value);

/**
 * Mirrors the `@validate(nonOptional())` decorators on {@link HealthInsurance} - the entity validator still
 * runs on commit, this one only exists so the user sees the German message instead of the engine default.
 */
const healthInsuranceValidator = {
  bezeichnung: sgNonOptional(),
  rahmenNr: sgNonOptional(),
  ort: sgNonOptional(),
  krkArt: sgNonOptional(),
  betriebsnummer: sgNonOptional(),
  iban: sgNonOptional(),
};

const HealthInsuranceModifyForm = stateful(
  (props: { insurance: HealthInsurance | null; resolve: () => void; reject: () => void }) => {
    // stable across rerenders of the subscription above; a new insurance starts as an empty entity
    const [insurance] = useState(props.insurance ?? new HealthInsurance({}), [props.insurance?._id]);
    const isNew = !insurance._id;

    const form = useForm(insurance, { validator: healthInsuranceValidator });
    const insuranceDocument = form.document;

    // the DateInput emits and consumes canonical calendar days (00:00:00.000Z of the day meant)
    const gueltigBisField = form.field('gueltigBis');
    const gueltigBis = insuranceDocument.gueltigBis ?? null;
    const isExpired = gueltigBis ? gueltigBis.getTime() < todayAsCalendarDay().getTime() : false;

    const handleSave = async () => {
      const formErrors = await form.validate();
      if (formErrors) {
        await openValidationErrorDialog({ error: formErrors, hardValidate: true });
        return;
      }

      await guardDialog(
        async () => {
          if (isNew) await insuranceDocument.create();
          else await insuranceDocument.save();
        },
        {
          success: () => {
            void SgSnackbar.show(`Krankenkasse wurde erfolgreich ${isNew ? 'erstellt' : 'gespeichert'}`);
            props.resolve();
          },
        },
      );
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: `Krankenkasse ${isNew ? 'hinzufügen' : 'bearbeiten'}`,
          close: () => props.reject(),
          closeLabel: 'Dialog schließen',
        })}

        <div class="form-grid">
          ${FormGridEntry({
            label: 'Bezeichnung*',
            render: TextInput({
              ...form.field('bezeichnung'),
              placeholder: 'Keine Bezeichnung hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Rahmen-Nr.*',
            render: TextInput({
              ...form.field('rahmenNr'),
              placeholder: 'Keine Rahmen-Nr. hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Ort*',
            render: TextInput({
              ...form.field('ort'),
              placeholder: 'Kein Ortsname hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Art der Krankenkasse*',
            render: DropdownSelectionInput({
              ...form.field('krkArt'),
              selectables: healthInsuranceTypeSelectables,
              placeholder: 'Keine Art hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Gültig bis',
            labelSubtext: gueltigBis
              ? `${isExpired ? 'Abgelaufen am' : 'Gültig bis'} ${formatCalendarDay(gueltigBis, 'dd.MM.yyyy')}`
              : 'Unbegrenzt gültig',
            render: DateInput({
              ...gueltigBisField,
              type: 'date',
              clearable: true,
              placeholder: 'Kein Ablaufdatum hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'Betriebsnummer*',
            render: NumberInput({
              ...form.field('betriebsnummer'),
              placeholder: 'Keine Betriebsnummer hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
          ${FormGridEntry({
            label: 'IBAN*',
            render: IBANInput({
              ...form.field('iban'),
              placeholder: 'Keine IBAN hinterlegt',
              placeholderMode: 'hide',
            }),
          })}
        </div>

        <div class="flex justify-end gap-2">
          <button class="btn btn-outline" @click=${() => props.reject()}>Abbrechen</button>
          <button class="btn btn-primary" @click=${handleSave}>Speichern</button>
        </div>
      </div>
    `;
  },
);

const HealthInsuranceModifyDialogContent = stateful(
  (props: { insuranceID: Maybe<string>; resolve: () => void; reject: () => void }) => {
    // resolves to null without a network roundtrip when no ID is given, i.e. when creating a new insurance
    const {
      value: insurance,
      loading,
      error,
    } = useQuery(
      () => subscribeByID(HealthInsurance, props.insuranceID)(HEALTH_INSURANCE_SELECTION),
      [props.insuranceID],
    );

    if (error || (!loading && props.insuranceID && !insurance))
      return html`
        <div class="dialog-container">
          ${DialogHeader({ title: 'Krankenkasse bearbeiten', close: () => props.reject(), closeLabel: 'Dialog schließen' })}
          <p class="text-error">
            ${error?.message ?? 'Die Krankenkasse konnte nicht geladen werden. Möglicherweise wurde sie gelöscht.'}
          </p>
        </div>
      `;

    if (loading)
      return html`
        <div class="dialog-container">
          ${DialogHeader({ title: 'Krankenkasse bearbeiten', close: () => props.reject(), closeLabel: 'Dialog schließen' })}
          <div class="form-grid">${Array.from({ length: 14 }, () => html`<div class="skeleton h-10"></div>`)}</div>
        </div>
      `;

    return HealthInsuranceModifyForm({
      // the subscription narrows the entity to its selection set, which drops MongoEntity's `create`.
      // The runtime value is a full HealthInsurance instance, so it is read back as one.
      insurance: insurance ? (insurance as HealthInsurance) : null,
      resolve: props.resolve,
      reject: props.reject,
    });
  },
);

/**
 * Opens the dialog to create or edit a health insurance. Without an `insuranceID` a new insurance is created.
 * Resolves once the dialog is gone - after a successful save or after it was dismissed.
 */
export const showHealthInsuranceModifyDialog = async (insuranceID?: Maybe<string>): Promise<void> => {
  try {
    await showDialog<void>({
      content: (resolve, reject) => HealthInsuranceModifyDialogContent({ insuranceID, resolve, reject }),
      modal: true,
      modalBoxClasses: 'max-w-xl',
    });
  } catch {
    // dialog was dismissed
  }
};
